import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { OVERALL_STATUS_LABELS, type StatusBannerResponse } from "@klantpunt/shared";
import { IconAlert, IconClose, IconInfo } from "../../app/icons";
import { api } from "../../lib/api";
import { useMe } from "../../lib/auth";
import "./status.css";

export const statusBannerKey = ["status", "banner"] as const;

export const useStatusBanner = () => {
  const { data: me } = useMe();
  return useQuery({
    queryKey: statusBannerKey,
    queryFn: () => api<StatusBannerResponse>("/status/banner"),
    enabled: Boolean(me),
    refetchInterval: 120_000,
    staleTime: 60_000,
  });
};

const fmt = (iso: string) =>
  new Date(iso).toLocaleString("nl-NL", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

function readDismissed(): string[] {
  try {
    return JSON.parse(sessionStorage.getItem("status-banner-weg") ?? "[]") as string[];
  } catch {
    return [];
  }
}

/** Banner bij een lopende storing of aankomend onderhoud met "toon banner" (docs/HELPCENTER.md §7.3). */
export function StatusBanner() {
  const q = useStatusBanner();
  const [dismissed, setDismissed] = useState(readDismissed);
  const items = (q.data?.banners ?? []).filter((b) => !dismissed.includes(b.id));
  if (!items.length) return null;
  const dismiss = (id: string) => {
    const next = [...dismissed, id];
    setDismissed(next);
    try {
      sessionStorage.setItem("status-banner-weg", JSON.stringify(next));
    } catch {
      /* geen opslag: alleen voor nu verbergen */
    }
  };
  return (
    <>
      {items.map((b) => (
        <div key={b.id} className={`subbanner stbanner st-${b.status}`} role="status" data-testid="status-banner">
          {b.status === "maintenance" ? (
            <IconInfo size={18} aria-hidden="true" />
          ) : (
            <IconAlert size={18} aria-hidden="true" />
          )}
          <span className="grow">
            <strong>
              {b.state === "scheduled"
                ? `Gepland onderhoud ${fmt(b.scheduledStart!)}`
                : b.kind === "maintenance"
                  ? "Onderhoud"
                  : OVERALL_STATUS_LABELS[b.status]}
              :
            </strong>{" "}
            {b.title}
            {q.data?.statusUrl && (
              <>
                {" "}
                <a href={q.data.statusUrl} target="_blank" rel="noopener">
                  Bekijk de status
                </a>
              </>
            )}
          </span>
          <button type="button" className="btn ghost ico" aria-label="Melding verbergen" onClick={() => dismiss(b.id)}>
            <IconClose size={18} aria-hidden="true" />
          </button>
        </div>
      ))}
    </>
  );
}

/** Eén regel met de actuele status, voor Hulp (§7.3). */
export function StatusLine() {
  const q = useStatusBanner();
  if (!q.data) return null;
  const label = OVERALL_STATUS_LABELS[q.data.overall];
  return (
    <p className={`stline st-${q.data.overall}`}>
      <span className="stdot" aria-hidden="true" />
      {q.data.statusUrl ? (
        <a href={q.data.statusUrl} target="_blank" rel="noopener">
          {label}
        </a>
      ) : (
        label
      )}
    </p>
  );
}
